// Stock values and costs (inventory's BR-INVENTORY-007, BR-INVENTORY-008, BR-INVENTORY-015; pricing's BR-PRICING-020;
// purchasing's BR-PURCHASING-007; decisions D14, W4, W5). Costs are ex-VAT cents; quantities are whole units (W4).
// Proven by TC-MONEY-017..022 (docs/test-cases/db.md, `packages/money/test/costing.test.ts`).
import type { Bp, Cents } from "@visionx/contracts";
import { assertAtLeast, assertInt, assertSafe, floorDiv, noNegativeZero, roundHalfUp } from "./arith";

/**
 * The weighted average cost of BR-INVENTORY-007 (D14, W5) after an arrival with a cost:
 * (on hand × average + received × unit cost) ÷ (on hand + received), half-up to the cent, where on hand counts all five
 * stock states. With nothing on hand the arrival's cost becomes the average and a stale average is ignored.
 * GRN-CEN-000019: `weightedAverageCost(1, 62000, 2, 62000)` = 62 000. Nothing received, a negative quantity or a negative
 * cost is a `RangeError`; a fraction anywhere a `TypeError`.
 */
export function weightedAverageCost(onHand: number, averageCost: Cents, receivedQty: number, unitCost: Cents): Cents {
  assertInt(onHand, "onHand"); assertInt(averageCost, "averageCost"); assertInt(receivedQty, "receivedQty"); assertInt(unitCost, "unitCost");
  assertAtLeast(onHand, 0, "onHand");
  assertAtLeast(averageCost, 0, "averageCost");
  assertAtLeast(receivedQty, 1, "receivedQty");
  assertAtLeast(unitCost, 0, "unitCost");
  if (onHand === 0) return unitCost;
  const held = onHand * averageCost;
  const arriving = receivedQty * unitCost;
  assertSafe(held, "on hand × average"); assertSafe(arriving, "received × unit cost");
  const total = held + arriving;
  assertSafe(total, "total cost");
  return roundHalfUp(total, onHand + receivedQty) as Cents;
}

/**
 * An amount times a whole number of units, exactly (W4): a stock line's value (BR-INVENTORY-008), a movement's total, a
 * transfer's or a variance's value, a priced line's total (BR-PRICING-010). Signed: `timesQuantity(38000, -1)` = −38 000.
 * A product beyond the safe integers is a `RangeError`, never a rounded number.
 */
export function timesQuantity(amount: Cents, quantity: number): Cents {
  assertInt(amount, "amount"); assertInt(quantity, "quantity");
  assertSafe(amount, "amount"); assertSafe(quantity, "quantity");
  const product = amount * quantity;
  assertSafe(product, "amount × quantity");
  return noNegativeZero(product) as Cents;
}

/**
 * A list price net of a discount by the supplier code tables' own formula (R-08, CAT-28; BR-PRICING-020, BR-PURCHASING-007):
 * net = (list × (10 000 − discount) + 5 000) ÷ 10 000 in whole cents — `netOfDiscount(100000, 4000)` = 60 000, R 600.00 —
 * not the list less a rounded percentage. A negative list, or a discount outside 0–10 000 bp, is a `RangeError`.
 */
export function netOfDiscount(list: Cents, discountBp: Bp): Cents {
  assertInt(list, "list"); assertInt(discountBp, "discountBp");
  assertAtLeast(list, 0, "list");
  if (discountBp < 0 || discountBp > 10_000) throw new RangeError(`discountBp must be 0 to 10 000 basis points, got ${discountBp}`);
  const numerator = list * (10_000 - discountBp) + 5_000;
  assertSafe(numerator, "list × (10 000 − discount)");
  return floorDiv(numerator, 10_000) as Cents;
}

/**
 * Takes a voided unit's own cost back out of the branch's average (BR-INVENTORY-015; D14, W5, W15), on hand counted before
 * the void: (on hand × average − unit cost) ÷ (on hand − 1), half-up, never below zero; when nothing remains the average
 * stands until the next arrival sets it. Menlyn's Silhouette: `backOutCost(3, 784000, 1960000)` = 196 000. Nothing on hand
 * or a negative cost is a `RangeError`; a fraction a `TypeError`.
 */
export function backOutCost(onHand: number, averageCost: Cents, unitCost: Cents): Cents {
  assertInt(onHand, "onHand"); assertInt(averageCost, "averageCost"); assertInt(unitCost, "unitCost");
  assertAtLeast(onHand, 1, "onHand");
  assertAtLeast(averageCost, 0, "averageCost");
  assertAtLeast(unitCost, 0, "unitCost");
  if (onHand === 1) return averageCost;
  const held = onHand * averageCost;
  assertSafe(held, "on hand × average");
  const remaining = held - unitCost;
  if (remaining <= 0) return 0 as Cents;
  return roundHalfUp(remaining, onHand - 1) as Cents;
}
